import experiment from 'experiment';
import type { CompLayout, CompRef, Layout } from 'types/documentServices';
import type { EditorAPI } from '#packages/editorAPI';
import type { Rect } from 'types/documentServices';

import type { SlotData } from '../types';
import type { SlotOpenerScope } from '../scope';
import { LINE_HEIGHT } from '../components/utils';

export function getAllSlotsForComponent(
  { editorAPI }: SlotOpenerScope,
  comp: CompRef[],
) {
  return getAllSlots(
    editorAPI,
    editorAPI.components.getContainer(comp),
    editorAPI.components.getSiblings(comp),
    editorAPI.components.layout.measure.getBoundingLayoutRelativeToScreen(comp),
  );
}

export function getAllSlotsInContainer(
  { editorAPI }: SlotOpenerScope,
  container: CompRef,
  dragBoxStageLayout: Layout,
) {
  return getAllSlots(
    editorAPI,
    container,
    editorAPI.components.getChildren(container),
    dragBoxStageLayout,
  );
}

/**
 * Find component above the slot by looking for the one whose bottomY is the lowest
 */
function getAboveComponent(
  editorAPI: EditorAPI,
  currentIdx: number,
  compRefs: CompRef[],
) {
  let aboveComponentIdx = currentIdx;

  const aboveCompLayout =
    editorAPI.components.layout.measure.getBoundingLayoutRelativeToScreen(
      compRefs[currentIdx],
    );

  const aboveCompBottomY = aboveCompLayout.y + aboveCompLayout.height;

  for (let i = currentIdx + 1; i < compRefs.length; i++) {
    const compRef = compRefs[i];
    const compLayout =
      editorAPI.components.layout.measure.getBoundingLayoutRelativeToScreen(
        compRef,
      );

    // There is a gap, so we are in the territory of belowComponent already
    if (compLayout.y >= aboveCompBottomY) {
      break;
    }

    // If component's bottomY is lower - this is the slot's aboveComponent
    if (compLayout.y + compLayout.height > aboveCompBottomY) {
      aboveComponentIdx = i;
    }
  }

  return aboveComponentIdx;
}

const isValidSlot = (slotLayout: Rect, selectedCompLayout: CompLayout) => {
  if (
    experiment.isOpen('se_slotOpenerV3') &&
    (slotLayout.height < LINE_HEIGHT ||
      slotLayout.height > selectedCompLayout.height - 1)
  ) {
    return false;
  }
  return true;
};

function getAllSlots(
  editorAPI: EditorAPI,
  container: CompRef,
  compRefs: CompRef[],
  selectedCompLayout: Layout,
) {
  const slotData = [] as SlotData[];
  const layoutGet =
    editorAPI.components.layout.measure.getBoundingLayoutRelativeToScreen;

  const isComponentAlreadyInSlot = (slotLayout: Rect) =>
    selectedCompLayout.y >= slotLayout.y &&
    selectedCompLayout.y + selectedCompLayout.height <=
      slotLayout.y + slotLayout.height;

  const compsSortedByY = [...compRefs].sort(
    (a, b) => layoutGet(a).y - layoutGet(b).y,
  );

  const containerLayout = layoutGet(container);
  const containerBottomY = containerLayout.y + containerLayout.height;

  let i = 0;
  while (i < compsSortedByY.length) {
    const aboveComponentIdx = getAboveComponent(editorAPI, i, compsSortedByY);
    const aboveComponent = compsSortedByY[aboveComponentIdx];
    const aboveCompLayout = layoutGet(aboveComponent);
    const aboveCompBottomY = aboveCompLayout.y + aboveCompLayout.height;

    // Slot between container's top edge and first component
    if (i === 0) {
      const firstComp = compsSortedByY[0];
      const firstCompLayout = layoutGet(firstComp);
      const slotLayout = {
        y: containerLayout.y,
        height: firstCompLayout.y - containerLayout.y,
        x: containerLayout.x,
        width: containerLayout.width,
      };

      if (
        !isComponentAlreadyInSlot(slotLayout) &&
        isValidSlot(slotLayout, selectedCompLayout)
      ) {
        slotData.push({
          aboveComponent: null,
          belowComponent: firstComp,
          layout: slotLayout,
          container,
        });
      }
    }

    const belowComponentIdx = aboveComponentIdx + 1;
    const belowComponent = compsSortedByY[belowComponentIdx];

    const isInFooter = !!editorAPI.sections.isFooter(
      editorAPI.sections.getClosestSectionLike(aboveComponent),
    );

    // If this is the last component, the slot is between it and containers bottom edge
    // Also, don't create bottommost slot in the footer because resizeToAndPush doesn't work properly
    if (belowComponent || !isInFooter) {
      const slotBottomY = belowComponent
        ? layoutGet(belowComponent).y
        : containerBottomY;

      const slotHeight = slotBottomY - aboveCompBottomY;
      const slotLayout = {
        y: aboveCompBottomY,
        height: slotHeight,
        x: containerLayout.x,
        width: containerLayout.width,
      };

      if (
        !isComponentAlreadyInSlot(slotLayout) &&
        isValidSlot(slotLayout, selectedCompLayout)
      ) {
        slotData.push({
          aboveComponent,
          belowComponent: belowComponent ? belowComponent : null,
          layout: slotLayout,
          container,
        });
      }
    }

    i = belowComponentIdx;
  }

  return slotData;
}
